import React, { useEffect, useState } from "react";
import { Refresh, GetApp } from "@material-ui/icons";
import { PlayArrow, Pause, VolumeOff, VolumeUp } from "@material-ui/icons";
import { Button, CircularProgress, IconButton, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { getServerUri } from "../modules/uriUtility";
import { incrementCompanyUsage } from "../modules/companySubscriptionApi";
import { noFolderCancelledMessage } from "../modules/buildProfileUtils";
import { isVersionedPlayEngineBase } from "../modules/baseKind";
import { sendCommand } from "../modules/playEngineBridge";
import { VERSION_BUILD_STAGE_LABELS, describeExportFailure, exportVersion, previewVersion, versionSizeEstimate } from "../modules/versionPlayEngineApi";
import BuildWaitLine from "./buildWaitLine";
import DialogMultipleVersionDownload from "./dialogMultipleVersionDownload";
import DialogVersionDownload from "./dialogVersionDownload";
import DialogPlayEngineExport from "./dialogPlayEngineExport";

import PlayOutIconDashboard from "../../images/playin/playOutIcon_dashboard.svg";

import _ from "lodash";
import PreviewDeviceControls from "./previewDeviceControls";
import ReactGA from "react-ga";
import ReactGA4 from "react-ga4";
import Popover from "@material-ui/core/Popover";
import DownloadIcon from "@mui/icons-material/Download";

const useStyles = makeStyles({
  middleDiv: {
    flex: 1,
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    lineHeight: "100%"
  },
  leftDiv: { textAlign: "left", flex: 1, padding: "10px", lineHeight: "100%" },
  rightDiv: { textAlign: "right", flex: 1, padding: "10px", lineHeight: "100%" },
  buildWaitLine: {
    display: "inline-block",
    maxWidth: "260px",
    marginRight: "8px",
    fontSize: "11px",
    color: "#aaaaaa",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    verticalAlign: "middle"
  },
  visible: { display: "inline" },
  muteButton: { color: "#fff", padding: "4px", marginLeft: "4px", alignSelf: "center" },
  estimatedSize: { color: "#9aa0a0", fontSize: "12px", marginRight: "10px", verticalAlign: "middle" },
  invisible: { display: "none" },
  downloadDialogCard: { margin: "15px", padding: 0, width: 440, backgroundColor: "#363636" },
  gamePreviewToolbarDiv: {
    height: "60px",
    minHeight: "60px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderStyle: "none none solid none",
    borderWidth: "1px",
    borderColor: "black"
  },
  downloadPlayable: {
    textTransform: "none",
    display: "flex",
    flexDirection: "row",
    flexWrap: "nowrap"
  },
  previewButton: {
    background: "none",
    border: "none",
    padding: 0,
    minWidth: "auto",
    marginRight: "6px",
    "&:hover": {
      background: "none"
    },
    "& img": {
      marginBottom: "-2px"
    }
  }
});

/**
 *
 * @param props.selectedVersion
 * @param props.setSelectedVersion {function}
 * @param props.selectedGame
 * @param props.selectedBase
 * @param props.devices
 * @param props.deviceId
 * @param props.isCustomSize
 * @param props.previewWidth
 * @param props.previewHeight
 * @param props.zoom
 * @param props.zoomSteps
 * @param props.effectiveZoom
 * @param props.onSelectZoom {function}
 * @param props.onSelectDevice {function}
 * @param props.onCustomSize {function}
 * @param props.onToggleOrientation {function}
 * @param props.isPortrait
 * @param props.iFrameId
 * @param props.setIsSaveDialogOpen
 * @param props.deleteVersionFromView
 * @param props.fetchVersions
 * @param props.isPreviewLoaded
 * @param props.setIsPreviewLoaded
 * @param props.refresh
 * @param props.userPermissions
 * @param props.createSnackbar
 * @param props.userCompany
 * @param props.setUserCompany
 * @param props.plans
 * @param props.classes
 * @param props.setIsDownloadingPlayable
 * @returns {JSX.Element|null}
 */
export default function toolbarGamePreview(props) {
  let [isDownloadDialogOpen, setIsDownloadDialogOpen] = useState(false);
  let [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  // Asked for per version rather than carried by the list: the estimate costs
  // a stat per asset, and a base can hold hundreds of versions nobody opened.
  const [estimatedBytes, setEstimatedBytes] = useState(null);
  useEffect(() => {
    if (!isPlayEngine || !selectedVersionIDString || selectedVersionIDString === "undefined") {
      setEstimatedBytes(null);
      return undefined;
    }
    let isCurrent = true;
    setEstimatedBytes(null);
    versionSizeEstimate(selectedVersionIDString)
      .then((result) => {
        if (isCurrent && result.ok) setEstimatedBytes(result.data.estimatedExportBytes);
      })
      .catch(() => undefined);
    return () => {
      isCurrent = false;
    };
  }, [isPlayEngine, selectedVersionIDString, props.selectedVersion?.dateUpdated]);
  const selectedVersionIDString = String(_.get(props, "selectedVersion._id"));
  const classes = useStyles();
  // Snapshot editor bases have their own toolbar; "engine" here is versionedPlayEngine.
  const isPlayEngine = isVersionedPlayEngineBase(props.selectedBase);

  // Utility: Safe fetch wrapper with consistent error handling
  async function safeFetch(endpoint, params) {
    const response = await fetch(getServerUri(endpoint), {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        accessToken: localStorage.getItem("session.accessToken"),
        ...params
      })
    });

    if (response.status !== 200) {
      const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
      throw new Error(errorData.message || "Server request failed");
    }

    return response.json();
  }

  // Utility: Check plan limits
  function checkPlanLimit(limitType) {
    const currentPeriod = props.userCompany.subscription.periods[props.userCompany.subscription.periods.length - 1];
    const currentPlan = props.plans[props.getPlanIndex(props.userCompany.subscription.currentPlan)];

    if (limitType === "test") {
      if (currentPeriod.testPlayableCount + 1 > currentPlan.numberOfTests) {
        props.createSnackbar(`Your company's current plan allows up to ${currentPlan.numberOfTests} playables`, "warning");
        return false;
      }
    } else if (limitType === "monthly") {
      if (currentPeriod.monthlyCreatedPlayables > currentPlan.numberOfPlayables) {
        props.createSnackbar(`Your company's current plan allows up to ${currentPlan.numberOfPlayables} playables`, "warning");
        return false;
      }
    }
    return true;
  }

  // Utility: Update counter (unified for both test and monthly)
  async function updateCounter(counterType = "testPlayableCount") {
    try {
      const newCompany = await incrementCompanyUsage(_.get(props.userCompany, "_id"), { [counterType]: 1 });
      props.setUserCompany(newCompany);
    } catch (err) {
      console.error("Failed to update counter:", err);
    }
  }

  const [isBuilding, setIsBuilding] = useState(false);
  const [isDownloadBuilding, setIsDownloadBuilding] = useState(false);
  // The running playEngine job's progress, for the wait line beside the spinner.
  const [playEngineJobEvents, setPlayEngineJobEvents] = useState([]);
  const [isMultipleVersionActive, setIsMultipleVersionActive] = useState(false);
  const [downloadIconColor, setDownloadIconColor] = useState("#ffffff1f");

  let [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    refresh();
  }, [props.refresh]);

  useEffect(() => {
    let color = "#eeeeee";
    if (!props.versionsAreFetched) {
      color = "#ffffff1f";
    }

    setDownloadIconColor(color);
  }, [props.versionsAreFetched]);

  const setValue = _.debounce((p) => {
    props.setSelectedVersionPathValue(p);
  }, 200);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  function closeAutoplay() {
    const isAutoplay = _.get(props, "selectedVersion.gameConfig.components.autoplay1.startAutoplay", null);
    if (!_.isNil(isAutoplay) && isAutoplay) {
      setValue({
        path: "gameConfig.components.autoplay1.startAutoplay",
        value: false
      });
    }
  }

  // Warn once for every network whose noFolder flag the server had to cancel
  function warnNoFolderCancelled(cancelledNetworks) {
    const message = noFolderCancelledMessage(cancelledNetworks);
    if (message) props.createSnackbar?.(message, "warning", false);
  }

  // Build all games sequentially
  async function buildAllGames(buildsToDo, baseDir, timeStamp, version, versionName) {
    const noFolderCancelled = [];

    for (const buildRecord of buildsToDo) {
      const buildResult = await safeFetch("version/buildGame", {
        baseDir,
        buildRecord: JSON.stringify(buildRecord),
        buildProfileData: JSON.stringify(props.userCompany?.buildProfileData || {}),
        gameTitle: props.selectedGame.title,
        baseTitle: props.selectedBase.title,
        timeStamp,
        version: JSON.stringify(version),
        versionName
      });
      noFolderCancelled.push(...(buildResult?.noFolderCancelledNetworks || []));
    }

    warnNoFolderCancelled(noFolderCancelled);
  }

  async function downloadTestPlayable() {
    if (!checkPlanLimit("test")) return;

    const language = [props.selectedVersion?.gameConfig.options.language];
    setIsDownloadBuilding(true);

    try {
      const dataPreBuild = await safeFetch("version/preBuildGame", {
        versionID: String(props.selectedVersion._id),
        networks: ["default"],
        languages: language
      });

      const buildsToDo = _.clone(dataPreBuild.buildsToDo);
      if (buildsToDo.length < 1) {
        console.error("There is no build to do");
        setIsDownloadBuilding(false);
        return;
      }

      const versionName = dataPreBuild.version.title.replaceAll(" ", "_");
      const { baseDir, timeStamp, version } = dataPreBuild;

      await buildAllGames(buildsToDo, baseDir, timeStamp, version, versionName);

      const uploadResult = await safeFetch("version/uploadGame", {
        baseDir,
        baseID: dataPreBuild.baseID,
        companyName: dataPreBuild.companyName,
        gameID: dataPreBuild.gameID,
        requestedSupportedLanguages: JSON.stringify(dataPreBuild.requestedSupportedLanguages),
        requestedSupportedNetworks: JSON.stringify(dataPreBuild.requestedSupportedNetworks),
        timeStamp,
        versionName,
        buildProfileData: JSON.stringify(props.userCompany?.buildProfileData || {}),
        gameTitle: props.selectedGame.title,
        baseTitle: props.selectedBase.title,
        // usage analytics: which version, and that this was a test download
        versionID: String(version._id),
        exportSource: "testDownload"
      });

      if (_.has(uploadResult, "file")) {
        setIsDownloadBuilding(false);
        window.location = uploadResult.file;
        props.setIsDownloadingPlayable(false);
        await updateCounter("testPlayableCount");
      }
    } catch (err) {
      console.error("Download test playable failed:", err);
      setIsDownloadBuilding(false);
      props.setIsDownloadingPlayable(false);
    }
  }



  // The two engine builds below run in the agentContainer as jobs
  // (controller/versionPlayEngine/*Version.js); everything customer-facing —
  // the plan counters, the preview record, the demo URL — stays here.

  async function downloadTestPlayablePlayEngine() {
    if (!checkPlanLimit("test")) return;

    const language = props.localizationLanguage || "en";
    setIsDownloadBuilding(true);
    setPlayEngineJobEvents([]);

    const result = await exportVersion({
      baseID: props.selectedBase._id,
      versionID: props.selectedVersion._id,
      networks: ["default"],
      languages: [language],
      // metadata.exportName → the delivered file is named exactly this
      // (buildProfile naming is skipped for it). Absent → normal naming.
      exportName: _.get(props, "selectedVersion.playEngineConfig.metadata.exportName", "") || "",
      gameTitle: props.selectedGame.title,
    }, { onProgress: setPlayEngineJobEvents });

    if (!result.ok || !_.has(result, "data.file")) {
      console.error("Download test playable (playEngine) failed:", result);
      props.createSnackbar?.("Test playable build failed: " + describeExportFailure(result), "error", false);
      setIsDownloadBuilding(false);
      props.setIsDownloadingPlayable(false);
      return;
    }

    warnNoFolderCancelled(result.data.noFolderCancelledNetworks || []);
    setIsDownloadBuilding(false);
    props.setIsDownloadingPlayable(false);
    window.location = result.data.file;
    await updateCounter("testPlayableCount");
  }

  async function onPreviewButtonPressedPlayEngine() {
    if (!checkPlanLimit("monthly") || !checkPlanLimit("test")) return;

    const language = props.localizationLanguage || "en";
    setIsBuilding(true);
    setPlayEngineJobEvents([]);
    console.info("[PlayEngine] Starting external preview build");
    props.setIsDownloadingPlayable(true);

    const result = await previewVersion({
      baseID: props.selectedBase._id,
      versionID: props.selectedVersion._id,
      language,
    }, { onProgress: setPlayEngineJobEvents });

    if (!result.ok || !_.has(result, "data.file")) {
      console.error("[PlayEngine] external preview failed:", result);
      props.createSnackbar?.("This playEngine game is not available for preview: " + describeExportFailure(result), "error");
      setIsBuilding(false);
      props.setIsDownloadingPlayable(false);
      return;
    }

    setIsBuilding(false);
    props.setIsDownloadingPlayable(false);

    const { file, previewData } = result.data;
    const fileKey = `?file=${previewData.timeStamp}_${previewData.language}_${previewData.fileName.replaceAll(".html", "")}`;
    const dashboardKey = "&dashboard=true";
    const previewURL = `https://demo.playin.com.tr/${fileKey}${dashboardKey}`;

    const siteDt = new Date().getTime();
    const linkObject = {
      versionName: String(props.selectedVersion.title),
      siteLink: siteDt,
      amazonLink: file.Location,
      amazonKey: file.Key,
      gameName: props.selectedGame.title,
      baseName: props.selectedBase.title,
      companyID: props.userCompany._id,
      demoURL: previewURL
    };

    savePreviewData(linkObject);
    window.open(previewURL);
    await updateCounter("testPlayableCount");
  }

  async function onPreviewButtonPressed() {
    if (!checkPlanLimit("monthly") || !checkPlanLimit("test")) return;
    setIsBuilding(true);
    console.info("Starting external preview build");
    props.setIsDownloadingPlayable(true);

    try {
      const dataPreBuild = await safeFetch("version/preBuildGame", {
        versionID: String(props.selectedVersion._id),
        networks: ["default"],
        languages: [props.localizationLanguage]
      });

      const buildsToDo = _.clone(dataPreBuild.buildsToDo);
      if (buildsToDo.length < 1) {
        console.error("There is no build to do");
        setIsBuilding(false);
        props.setIsDownloadingPlayable(false);
        return;
      }

      const versionName = dataPreBuild.version.title.replaceAll(" ", "_");
      const { baseDir, timeStamp, version } = dataPreBuild;

      await buildAllGames(buildsToDo, baseDir, timeStamp, version, versionName);

      const uploadResult = await safeFetch("version/uploadExternalPreview", {
        baseDir,
        baseID: dataPreBuild.baseID,
        companyName: dataPreBuild.companyName,
        gameID: dataPreBuild.gameID,
        language: props.localizationLanguage,
        timeStamp,
        versionName,
        buildProfileData: JSON.stringify(props.userCompany?.buildProfileData || {}),
        gameTitle: props.selectedGame.title,
        baseTitle: props.selectedBase.title
      });

      if (_.has(uploadResult, "file")) {
        setIsBuilding(false);
        props.setIsDownloadingPlayable(false);

        const previewData = uploadResult.previewData;

        const fileKey = `?file=${previewData.timeStamp}_${previewData.lanugage}_${previewData.fileName.replaceAll(".html", "")}`;
        const dashboardKey = "&dashboard=true";
        const previewURL = `https://demo.playin.com.tr/${fileKey}${dashboardKey}`;

        const siteDt = new Date().getTime();
        const linkObject = {
          versionName: String(props.selectedVersion.title),
          siteLink: siteDt,
          amazonLink: uploadResult.file.Location,
          amazonKey: uploadResult.file.Key,
          gameName: props.selectedGame.title,
          baseName: props.selectedBase.title,
          companyID: props.userCompany._id,
          demoURL: previewURL
        };

        savePreviewData(linkObject);

        window.open(previewURL);
        await updateCounter("testPlayableCount");
      }
    } catch (err) {
      props.createSnackbar("This game is not available for preview. Please contact the Playin Support Team.", "error");
      setIsBuilding(false);
      props.setIsDownloadingPlayable(false);
    }
  }

  /** The engine's own mute, dispatched into the preview iframe. */
  function toggleMute() {
    const next = !isMuted;
    setIsMuted(next);
    sendCommand(props.iFrameId, {
      type: "playengine:dispatch-action",
      action: { target: "engine", name: next ? "muteGame" : "unmuteGame" },
    }).catch((err) => console.error("Could not mute the preview:", err));
  }

  function refresh() {
    closeAutoplay();

    setIsPaused(false);
    // A reloaded playable starts unmuted, so the button must not claim otherwise.
    setIsMuted(false);
    props.setIsPreviewLoaded(false);
    if (isPlayEngine) {
      props.onPlayEngineRefresh?.();
    } else {
      document.getElementById(props.iFrameId).src += "";
    }

    try {
      document.getElementById(props.iFrameId).focus();
    } catch (err) {
      console.error(err);
    }
  }
  async function savePreviewData(linkObject) {
    try {
      await safeFetch("version/savePreviewData", {
        versionName: linkObject.versionName,
        siteLink: linkObject.siteLink,
        amazonLink: linkObject.amazonLink,
        amazonKey: linkObject.amazonKey,
        gameName: linkObject.gameName,
        baseName: linkObject.baseName,
        companyID: linkObject.companyID,
        demoURL: linkObject.demoURL
      });
    } catch (err) {
      console.error("Failed to save preview data:", err);
    }
  }

  if (_.isNil(selectedVersionIDString)) return null;
  return (
    <div id={"gamePreviewToolbarDiv"} className={classes.gamePreviewToolbarDiv}>
      <div id={"leftDiv"} className={classes.leftDiv}>
        {/* What this version exports to, worked out server-side the way the
            snapshot editor works it out (versionPlayEngine/versionSizeEstimate.js).
            An estimate, not a measurement: it is deliberately fitted never to
            read below the html that comes out. */}
        {isPlayEngine && Number.isFinite(estimatedBytes) && (
          <Tooltip title="Estimated size of the exported html; fitted never to read below the real one">
            <span className={classes.estimatedSize}>~{(estimatedBytes / 1e6).toFixed(2)} MB</span>
          </Tooltip>
        )}
        <Pause
          className={isPaused ? classes.invisible : classes.visible}
          onClick={() => {
            setIsPaused(true);
            ReactGA.event({
              category: "Preview",
              action: "Pause Clicked",
              label:
                "game: " +
                props.selectedGame.title +
                ", base: " +
                props.selectedBase.title +
                ", version: " +
                props.selectedVersion.title,
              dimension1: localStorage.getItem("user.id")
            });
            ReactGA4.event({
              category: "Preview",
              action: "Pause Clicked",
              label:
                "game: " +
                props.selectedGame.title +
                ", base: " +
                props.selectedBase.title +
                ", version: " +
                props.selectedVersion.title,
              dimension1: localStorage.getItem("user.id")
            });
            document.getElementById("gamePreviewIframe").contentWindow.setIsPaused(true);
          }}
        ></Pause>
        <PlayArrow
          className={!isPaused ? classes.invisible : classes.visible}
          onClick={() => {
            setIsPaused(false);
            ReactGA.event({
              category: "Preview",
              action: "Play Clicked",
              label:
                "game: " +
                props.selectedGame.title +
                ", base: " +
                props.selectedBase.title +
                ", version: " +
                props.selectedVersion.title,
              dimension1: localStorage.getItem("user.id")
            });
            ReactGA4.event({
              category: "Preview",
              action: "Play Clicked",
              label:
                "game: " +
                props.selectedGame.title +
                ", base: " +
                props.selectedBase.title +
                ", version: " +
                props.selectedVersion.title,
              dimension1: localStorage.getItem("user.id")
            });
            document.getElementById("gamePreviewIframe").contentWindow.setIsPaused(false);
            try {
              document.getElementById(props.iFrameId).focus();
            } catch (err) {
              console.error(err);
            }
          }}
        ></PlayArrow>

        <Refresh
          onClick={() => {
            refresh();
            ReactGA.event({
              category: "Preview",
              action: "Refresh Clicked",
              label:
                "game: " +
                props.selectedGame.title +
                ", base: " +
                props.selectedBase.title +
                ", version: " +
                props.selectedVersion.title,
              dimension1: localStorage.getItem("user.id")
            });
            ReactGA4.event({
              category: "Preview",
              action: "Refresh Clicked",
              label:
                "game: " +
                props.selectedGame.title +
                ", base: " +
                props.selectedBase.title +
                ", version: " +
                props.selectedVersion.title,
              dimension1: localStorage.getItem("user.id")
            });
          }}
        ></Refresh>

      </div>
      <div className={classes.middleDiv}>
        <PreviewDeviceControls
          isPortrait={props.isPortrait}
          devices={props.devices}
          deviceId={props.deviceId}
          isCustomSize={props.isCustomSize}
          previewWidth={props.previewWidth}
          previewHeight={props.previewHeight}
          zoom={props.zoom}
          zoomSteps={props.zoomSteps}
          effectiveZoom={props.effectiveZoom}
          onSelectZoom={props.onSelectZoom}
          onSelectDevice={props.onSelectDevice}
          onCustomSize={props.onCustomSize}
          onToggleOrientation={props.onToggleOrientation}
        />
        {/* Mute is the preview's, not the version's: it dispatches the engine's
            own muteGame/unmuteGame action rather than writing masterVolume, so
            nothing about the playable changes. Only the playEngine exposes it.
            It sits with the device controls, as the pe3d toolbar's does. */}
        {isPlayEngine && (
          <Tooltip title={isMuted ? "Unmute the preview" : "Mute the preview"}>
            <IconButton size="small" className={classes.muteButton} onClick={toggleMute}>
              {isMuted ? <VolumeOff fontSize="small" /> : <VolumeUp fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </div>
      <div className={classes.rightDiv}>
        {isPlayEngine && (isBuilding || isDownloadBuilding) && (
          <BuildWaitLine
            events={playEngineJobEvents}
            stageLabels={VERSION_BUILD_STAGE_LABELS}
            className={classes.buildWaitLine}
          />
        )}
        {/* {process.env.NODE_ENV === "production" && ( */}
        <>
          <CircularProgress
            size="1.2rem"
            color={"inherit"}
            style={{
              textAlign: "center",
              display: isBuilding ? "inline-block" : "none",
              marginRight: "8px",
              marginBottom: "-3px"
            }}
          ></CircularProgress>
          <Button
            className={classes.previewButton}
            disabled={isBuilding}
            style={{
              display: isBuilding ? "none" : "inline-block"
            }}
            onClick={isPlayEngine ? onPreviewButtonPressedPlayEngine : onPreviewButtonPressed}
            disableRipple
          >
            <img src={PlayOutIconDashboard} alt={"Preview Button"} />
          </Button>
        </>
        {/* // )} */}

        <GetApp
          style={{
            marginBottom: "-5.5px",
            display: isDownloadBuilding ? "none" : "inline-block",
            color: downloadIconColor
          }}
          onClick={(event) => {
            if (!props.versionsAreFetched) {
              return;
            }

            if (
              _.has(props.userCompany, "isPermittedMultipleVersionDownload") &&
              props.userCompany.isPermittedMultipleVersionDownload === true
            ) {
              setIsMultipleVersionActive(true);
              handleClick(event);
            } else {
              setIsDownloadDialogOpen(true);
            }
          }}
        ></GetApp>
        {isMultipleVersionActive && (
          <CircularProgress
            size="1.2rem"
            color={"inherit"}
            style={{
              marginRight: "8px",
              marginBottom: "-3px",
              textAlign: "center",
              display: isDownloadBuilding ? "inline-block" : "none"
            }}
          ></CircularProgress>
        )}
        {isMultipleVersionActive && (
          <Popover
            id="simplePopper_downloadVersion"
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left"
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left"
            }}
          >
            <div>
              <Button
                id="downloadVersions_button"
                className={classes.downloadPlayable}
                style={{
                  width: "200px",
                  justifyContent: "flex-start",
                  fontSize: "16px"
                }}
                onClick={() => {
                  setAnchorEl(false);
                  setIsDownloadDialogOpen(true);
                }}
              >
                <DownloadIcon style={{ width: "20px", height: "20px", marginRight: "4px" }} />
                Production Playable
              </Button>
              <Button
                id="downloadDefaultNetwork_button"
                className={classes.downloadPlayable}
                style={{
                  width: "200px",
                  justifyContent: "flex-start",
                  fontSize: "16px",
                  display: "flex"
                }}
                onClick={() => {
                  setAnchorEl(false);

                  if (isPlayEngine) {
                    downloadTestPlayablePlayEngine();
                  } else {
                    downloadTestPlayable();
                  }
                }}
              >
                <DownloadIcon style={{ width: "20px", height: "20px", marginRight: "4px" }} />
                Test Playable
              </Button>
            </div>
          </Popover>
        )}
        {isPlayEngine && (
          <DialogPlayEngineExport
            onClose={() => {
              setIsDownloadDialogOpen(false);
            }}
            setIsDownloadDialogOpen={(value) => {
              setIsDownloadDialogOpen(value);
            }}
            isOpen={isDownloadDialogOpen}
            selectedVersion={props.selectedVersion}
            selectedGame={props.selectedGame}
            selectedBase={props.selectedBase}
            userCompany={props.userCompany}
            setUserCompany={props.setUserCompany}
            createSnackbar={props.createSnackbar}
            plans={props.plans}
            getPlanIndex={props.getPlanIndex}
            setIsDownloadingPlayable={props.setIsDownloadingPlayable}
            versions={props.versions}
            versionHierarchy={props.versionHierarchy}
          />
        )}

        {!isPlayEngine && isMultipleVersionActive && (
          <DialogMultipleVersionDownload
            onClose={() => {
              setIsDownloadDialogOpen(false);
            }}
            setIsDownloadDialogOpen={(value) => {
              setIsDownloadDialogOpen(value);
            }}
            isOpen={isDownloadDialogOpen}
            totalSizeValue={props.totalSizeValue}
            versionUpdate={props.versionUpdate}
            selectedVersion={props.selectedVersion}
            selectedGame={props.selectedGame}
            selectedBase={props.selectedBase}
            userCompany={props.userCompany}
            setUserCompany={props.setUserCompany}
            classes={props.classes}
            createSnackbar={props.createSnackbar}
            plans={props.plans}
            versions={props.versions}
            versionRename={props.versionRename}
            getPlanIndex={props.getPlanIndex}
            setIsDownloadingPlayable={props.setIsDownloadingPlayable}
            localizationLanguage={props.localizationLanguage}
          ></DialogMultipleVersionDownload>
        )}

        {!isPlayEngine && !isMultipleVersionActive && (
          <DialogVersionDownload
            onClose={() => {
              setIsDownloadDialogOpen(false);
            }}
            setIsDownloadDialogOpen={(value) => {
              setIsDownloadDialogOpen(value);
            }}
            isOpen={isDownloadDialogOpen}
            totalSizeValue={props.totalSizeValue}
            versionUpdate={props.versionUpdate}
            selectedVersion={props.selectedVersion}
            selectedGame={props.selectedGame}
            selectedBase={props.selectedBase}
            userCompany={props.userCompany}
            setUserCompany={props.setUserCompany}
            classes={props.classes}
            createSnackbar={props.createSnackbar}
            plans={props.plans}
            versions={props.versions}
            versionRename={props.versionRename}
            getPlanIndex={props.getPlanIndex}
            setIsDownloadingPlayable={props.setIsDownloadingPlayable}
          ></DialogVersionDownload>
        )}
      </div>
    </div>
  );
}
